/* =============================================================
   BOOKINGS — DATA ACCESS
   =============================================================
   Read directly; changed ONLY through the database functions
   respond_to_booking and cancel_booking. The table has no insert
   or update policy at all -- see
   supabase/migrations/20261004113020_create_bookings.sql.

   RLS shows each company the bookings it made and the bookings
   at its berths -- both sides of the same row.
   ============================================================= */

import { supabase } from './supabase'
import { toDataError } from './errors'
import type { AmenityCode, Booking } from './types'

/* Three many-to-one embeds, aliased singular -- the same pattern and the same
 * `overrideTypes` reason as berths.ts. */
const COLUMNS =
  'id, reference, berth_id, vessel_id, company_id, arrival, departure, status, price_eur, rate_id, ' +
  'requested_amenities, decided_at, cancelled_at, created_at, ' +
  'berth:berths(id, name, city, company_id, booking_mode), ' +
  'vessel:vessels(id, name, length_m), ' +
  'company:companies(id, name)'

export interface BookingFilter {
  berthId?: number
  vesselId?: number
  companyId?: number
}

/** Newest arrival first: what is coming up, then what has been. */
export async function listBookings(filter: BookingFilter = {}): Promise<Booking[]> {
  let query = supabase.from('bookings').select(COLUMNS)
  if (filter.berthId !== undefined) query = query.eq('berth_id', filter.berthId)
  if (filter.vesselId !== undefined) query = query.eq('vessel_id', filter.vesselId)
  if (filter.companyId !== undefined) query = query.eq('company_id', filter.companyId)

  const { data, error } = await query
    .order('arrival', { ascending: false })
    .overrideTypes<Booking[], { merge: false }>()

  if (error) throw toDataError(error, 'Could not load bookings.')
  return data ?? []
}

export async function getBooking(id: number): Promise<Booking | null> {
  const { data, error } = await supabase
    .from('bookings')
    .select(COLUMNS)
    .eq('id', id)
    .maybeSingle()
    .overrideTypes<Booking, { merge: false }>()

  if (error) throw toDataError(error, 'Could not load the booking.')
  return data
}

/** Requests waiting for an owner -- the number worth seeing on the dashboard. */
export async function countPendingBookings(): Promise<number> {
  const { count, error } = await supabase
    .from('bookings')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'requested')

  if (error) throw toDataError(error, 'Could not count bookings.')
  return count ?? 0
}

export async function countBookings(): Promise<number> {
  const { count, error } = await supabase
    .from('bookings')
    .select('id', { count: 'exact', head: true })

  if (error) throw toDataError(error, 'Could not count bookings.')
  return count ?? 0
}

/**
 * Book a berth, as the signed-in company. The database checks the vessel is
 * yours, fits, the amenities are offered and the slot is free, prices the
 * stay with stay_price(), and confirms at once (instant berth) or leaves it
 * `requested` for the owner (manual berth). Returns the new booking's id.
 */
export async function requestBooking(input: {
  berthId: number
  vesselId: number
  arrival: string
  departure: string
  amenities: AmenityCode[]
}): Promise<number> {
  const { data, error } = await supabase.rpc('request_booking', {
    p_berth_id: input.berthId,
    p_vessel_id: input.vesselId,
    p_arrival: input.arrival,
    p_departure: input.departure,
    p_amenities: input.amenities,
  })
  if (error) throw toDataError(error, 'Could not place the booking.')
  return (data as { id: number }).id
}

/** Accept or reject a request, as the berth owner. */
export async function respondToBooking(id: number, accept: boolean): Promise<void> {
  const { error } = await supabase.rpc('respond_to_booking', {
    p_booking_id: id,
    p_accept: accept,
  })
  if (error) throw toDataError(error, accept ? 'Could not confirm.' : 'Could not reject.')
}

/** Cancel before arrival, as the booker. No fee in the demo. */
export async function cancelBooking(id: number): Promise<void> {
  const { error } = await supabase.rpc('cancel_booking', { p_booking_id: id })
  if (error) throw toDataError(error, 'Could not cancel the booking.')
}
